import type { ContentBuilder } from '../../ui/ContentBuilder';
import { getPopulationCount } from '../../../engine/map/settlement';
import {
    MAX_BUILDERS,
    MAX_BURNERS,
    MAX_FORESTERS,
    MAX_FISHERS,
    MAX_LUMBERJACKS,
    MAX_MASONS,
    MAX_MINERS,
    MAX_QUARRYMEN,
    MAX_SMELTERS,
    MAX_SAWMILLERS,
    MAX_SMOKERS,
    MAX_SMITHS,
    MAX_GOLD_MINERS,
    MAX_MINTERS,
    MAX_WEAVERS,
    MAX_MILITIA,
} from '../../../engine/map/settlement';
import type { SettlementView } from '../utils';
import {
    ACTION__MODAL__TAB__JOBS__CORE,
    ACTION__MODAL__TAB__JOBS__FOOD,
    ACTION__MODAL__TAB__JOBS__INDUSTRY,
    ACTION__MODAL__TAB__JOBS__MINING,
    ACTION__MODAL__TAB__JOBS__CRAFT,
    ACTION__MODAL__TAB__JOBS__SUPPORT,
    ACTION__SETTLEMENT__FIRE_ASSISTANT,
    ACTION__SETTLEMENT__FIRE_BUILDER,
    ACTION__SETTLEMENT__FIRE_BURNER,
    ACTION__SETTLEMENT__FIRE_MASON,
    ACTION__SETTLEMENT__FIRE_MINER,
    ACTION__SETTLEMENT__FIRE_QUARRYMAN,
    ACTION__SETTLEMENT__FIRE_SMELTER,
    ACTION__SETTLEMENT__FIRE_FARMER,
    ACTION__SETTLEMENT__FIRE_FORESTER,
    ACTION__SETTLEMENT__FIRE_HUNTER,
    ACTION__SETTLEMENT__FIRE_FISHER,
    ACTION__SETTLEMENT__FIRE_SMOKER,
    ACTION__SETTLEMENT__FIRE_LUMBERJACK,
    ACTION__SETTLEMENT__FIRE_SAWMILLER,
    ACTION__SETTLEMENT__FIRE_WEAVER,
    ACTION__SETTLEMENT__FIRE_SMITH,
    ACTION__SETTLEMENT__FIRE_GOLD_MINER,
    ACTION__SETTLEMENT__FIRE_MINTER,
    ACTION__SETTLEMENT__FIRE_MILITIA,
    ACTION__SETTLEMENT__HIRE_ASSISTANT,
    ACTION__SETTLEMENT__HIRE_BUILDER,
    ACTION__SETTLEMENT__HIRE_BURNER,
    ACTION__SETTLEMENT__HIRE_MASON,
    ACTION__SETTLEMENT__HIRE_MINER,
    ACTION__SETTLEMENT__HIRE_QUARRYMAN,
    ACTION__SETTLEMENT__HIRE_SMELTER,
    ACTION__SETTLEMENT__HIRE_FARMER,
    ACTION__SETTLEMENT__HIRE_FORESTER,
    ACTION__SETTLEMENT__HIRE_HUNTER,
    ACTION__SETTLEMENT__HIRE_FISHER,
    ACTION__SETTLEMENT__HIRE_SMOKER,
    ACTION__SETTLEMENT__HIRE_LUMBERJACK,
    ACTION__SETTLEMENT__HIRE_SAWMILLER,
    ACTION__SETTLEMENT__HIRE_WEAVER,
    ACTION__SETTLEMENT__HIRE_SMITH,
    ACTION__SETTLEMENT__HIRE_GOLD_MINER,
    ACTION__SETTLEMENT__HIRE_MINTER,
    ACTION__SETTLEMENT__HIRE_MILITIA,
} from '../../../engine/input/actionIds';
import { t, type Language } from '../../../i18n/translations';

export function renderJobsTab(
    builder: ContentBuilder,
    settlement: SettlementView,
    free: number,
    lang: Language,
    activeTabId: string,
): void {
    builder.tabs([
        {
            id: ACTION__MODAL__TAB__JOBS__CORE,
            label: t(lang, 'tabs.jobs.core'),
            active: activeTabId === ACTION__MODAL__TAB__JOBS__CORE,
        },
        {
            id: ACTION__MODAL__TAB__JOBS__FOOD,
            label: t(lang, 'tabs.jobs.food'),
            active: activeTabId === ACTION__MODAL__TAB__JOBS__FOOD,
        },
        {
            id: ACTION__MODAL__TAB__JOBS__INDUSTRY,
            label: t(lang, 'tabs.jobs.industry'),
            active: activeTabId === ACTION__MODAL__TAB__JOBS__INDUSTRY,
        },
        {
            id: ACTION__MODAL__TAB__JOBS__MINING,
            label: t(lang, 'tabs.jobs.mining'),
            active: activeTabId === ACTION__MODAL__TAB__JOBS__MINING,
        },
        {
            id: ACTION__MODAL__TAB__JOBS__CRAFT,
            label: t(lang, 'tabs.jobs.craft'),
            active: activeTabId === ACTION__MODAL__TAB__JOBS__CRAFT,
        },
        {
            id: ACTION__MODAL__TAB__JOBS__SUPPORT,
            label: t(lang, 'tabs.jobs.support'),
            active: activeTabId === ACTION__MODAL__TAB__JOBS__SUPPORT,
        },
    ]);
    builder.spacer(6);

    builder.section(t(lang, 'jobs.section'), (section) => {
        section.text(t(lang, 'jobs.free', free));
        section.spacer(4);

        if (activeTabId === ACTION__MODAL__TAB__JOBS__CORE) {
            section.text(
                t(
                    lang,
                    'jobs.lumberjacks',
                    settlement.jobs.lumberjacks,
                    MAX_LUMBERJACKS * settlement.buildings.lumberjack.qty,
                ),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_LUMBERJACK,
                t(lang, 'jobs.lumberjackPlus'),
                settlement.buildings.lumberjack.qty > 0 &&
                    settlement.jobs.lumberjacks <
                        MAX_LUMBERJACKS * settlement.buildings.lumberjack.qty &&
                    free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_LUMBERJACK,
                t(lang, 'jobs.lumberjackMinus'),
                settlement.jobs.lumberjacks > 0,
            );
            section.spacer(4);

            section.text(
                t(
                    lang,
                    'jobs.assistants',
                    settlement.jobs.assistants,
                    getPopulationCount(settlement),
                ),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_ASSISTANT,
                t(lang, 'jobs.assistantPlus'),
                free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_ASSISTANT,
                t(lang, 'jobs.assistantMinus'),
                settlement.jobs.assistants > 0,
            );
            section.spacer(4);

            section.text(
                t(
                    lang,
                    'jobs.sawmillers',
                    settlement.jobs.sawmillers,
                    MAX_SAWMILLERS * settlement.buildings.sawmill.qty,
                ),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_SAWMILLER,
                t(lang, 'jobs.sawmillerPlus'),
                settlement.buildings.sawmill.qty > 0 &&
                    settlement.jobs.sawmillers <
                        MAX_SAWMILLERS * settlement.buildings.sawmill.qty &&
                    free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_SAWMILLER,
                t(lang, 'jobs.sawmillerMinus'),
                settlement.jobs.sawmillers > 0,
            );
            section.spacer(4);

            section.text(
                t(
                    lang,
                    'jobs.foresters',
                    settlement.jobs.foresters,
                    MAX_FORESTERS * settlement.buildings.lumberjack.qty,
                ),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_FORESTER,
                t(lang, 'jobs.foresterPlus'),
                settlement.buildings.lumberjack.qty > 0 &&
                    settlement.jobs.foresters <
                        MAX_FORESTERS * settlement.buildings.lumberjack.qty &&
                    free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_FORESTER,
                t(lang, 'jobs.foresterMinus'),
                settlement.jobs.foresters > 0,
            );
            section.spacer(4);
        }

        if (activeTabId === ACTION__MODAL__TAB__JOBS__FOOD) {
            section.text(
                t(lang, 'jobs.farmers', settlement.jobs.farmers, settlement.buildings.farm.qty),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_FARMER,
                t(lang, 'jobs.farmerPlus'),
                settlement.buildings.farm.qty > 0 &&
                    settlement.jobs.farmers < settlement.buildings.farm.qty &&
                    free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_FARMER,
                t(lang, 'jobs.farmerMinus'),
                settlement.jobs.farmers > 0,
            );
            section.spacer(4);

            section.text(
                t(
                    lang,
                    'jobs.hunters',
                    settlement.jobs.hunters,
                    settlement.buildings.huntingHut.qty,
                ),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_HUNTER,
                t(lang, 'jobs.hunterPlus'),
                settlement.buildings.huntingHut.qty > 0 &&
                    settlement.jobs.hunters < settlement.buildings.huntingHut.qty &&
                    free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_HUNTER,
                t(lang, 'jobs.hunterMinus'),
                settlement.jobs.hunters > 0,
            );
            section.spacer(4);

            section.text(
                t(
                    lang,
                    'jobs.fishers',
                    settlement.jobs.fishers,
                    MAX_FISHERS * settlement.buildings.fishingHut.qty,
                ),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_FISHER,
                t(lang, 'jobs.fisherPlus'),
                settlement.buildings.fishingHut.qty > 0 &&
                    settlement.jobs.fishers < MAX_FISHERS * settlement.buildings.fishingHut.qty &&
                    free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_FISHER,
                t(lang, 'jobs.fisherMinus'),
                settlement.jobs.fishers > 0,
            );
            section.spacer(4);

            section.text(
                t(
                    lang,
                    'jobs.smokers',
                    settlement.jobs.smokers,
                    MAX_SMOKERS * settlement.buildings.smokehouse.qty,
                ),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_SMOKER,
                t(lang, 'jobs.smokerPlus'),
                settlement.buildings.smokehouse.qty > 0 &&
                    settlement.jobs.smokers < MAX_SMOKERS * settlement.buildings.smokehouse.qty &&
                    free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_SMOKER,
                t(lang, 'jobs.smokerMinus'),
                settlement.jobs.smokers > 0,
            );
            section.spacer(4);
        }

        if (activeTabId === ACTION__MODAL__TAB__JOBS__INDUSTRY) {
            section.text(
                t(
                    lang,
                    'jobs.burners',
                    settlement.jobs.burners,
                    MAX_BURNERS * settlement.buildings.charcoalPit.qty,
                ),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_BURNER,
                t(lang, 'jobs.burnerPlus'),
                settlement.buildings.charcoalPit.qty > 0 &&
                    settlement.jobs.burners < MAX_BURNERS * settlement.buildings.charcoalPit.qty &&
                    free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_BURNER,
                t(lang, 'jobs.burnerMinus'),
                settlement.jobs.burners > 0,
            );
            section.spacer(4);

            section.text(
                t(
                    lang,
                    'jobs.smelters',
                    settlement.jobs.smelters,
                    MAX_SMELTERS * settlement.buildings.smelter.qty,
                ),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_SMELTER,
                t(lang, 'jobs.smelterPlus'),
                settlement.buildings.smelter.qty > 0 &&
                    settlement.jobs.smelters < MAX_SMELTERS * settlement.buildings.smelter.qty &&
                    free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_SMELTER,
                t(lang, 'jobs.smelterMinus'),
                settlement.jobs.smelters > 0,
            );
            section.spacer(4);
        }

        if (activeTabId === ACTION__MODAL__TAB__JOBS__MINING) {
            section.text(
                t(
                    lang,
                    'jobs.quarrymen',
                    settlement.jobs.quarrymen,
                    MAX_QUARRYMEN * settlement.buildings.quarry.qty,
                ),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_QUARRYMAN,
                t(lang, 'jobs.quarrymanPlus'),
                settlement.buildings.quarry.qty > 0 &&
                    settlement.jobs.quarrymen < MAX_QUARRYMEN * settlement.buildings.quarry.qty &&
                    free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_QUARRYMAN,
                t(lang, 'jobs.quarrymanMinus'),
                settlement.jobs.quarrymen > 0,
            );
            section.spacer(4);

            section.text(
                t(
                    lang,
                    'jobs.masons',
                    settlement.jobs.masons,
                    MAX_MASONS * settlement.buildings.masonry.qty,
                ),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_MASON,
                t(lang, 'jobs.masonPlus'),
                settlement.buildings.masonry.qty > 0 &&
                    settlement.jobs.masons < MAX_MASONS * settlement.buildings.masonry.qty &&
                    free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_MASON,
                t(lang, 'jobs.masonMinus'),
                settlement.jobs.masons > 0,
            );
            section.spacer(4);

            section.text(
                t(
                    lang,
                    'jobs.miners',
                    settlement.jobs.miners,
                    MAX_MINERS * settlement.buildings.ironMine.qty,
                ),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_MINER,
                t(lang, 'jobs.minerPlus'),
                settlement.buildings.ironMine.qty > 0 &&
                    settlement.jobs.miners < MAX_MINERS * settlement.buildings.ironMine.qty &&
                    free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_MINER,
                t(lang, 'jobs.minerMinus'),
                settlement.jobs.miners > 0,
            );
            section.spacer(4);

            section.text(
                t(
                    lang,
                    'jobs.goldMiners',
                    settlement.jobs.goldMiners,
                    MAX_GOLD_MINERS * settlement.buildings.goldMine.qty,
                ),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_GOLD_MINER,
                t(lang, 'jobs.goldMinerPlus'),
                settlement.buildings.goldMine.qty > 0 &&
                    settlement.jobs.goldMiners <
                        MAX_GOLD_MINERS * settlement.buildings.goldMine.qty &&
                    free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_GOLD_MINER,
                t(lang, 'jobs.goldMinerMinus'),
                settlement.jobs.goldMiners > 0,
            );
            section.spacer(4);
        }

        if (activeTabId === ACTION__MODAL__TAB__JOBS__CRAFT) {
            section.text(
                t(
                    lang,
                    'jobs.weavers',
                    settlement.jobs.weavers,
                    MAX_WEAVERS * settlement.buildings.weaver.qty,
                ),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_WEAVER,
                t(lang, 'jobs.weaverPlus'),
                settlement.buildings.weaver.qty > 0 &&
                    settlement.jobs.weavers < MAX_WEAVERS * settlement.buildings.weaver.qty &&
                    free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_WEAVER,
                t(lang, 'jobs.weaverMinus'),
                settlement.jobs.weavers > 0,
            );
            section.spacer(4);

            section.text(
                t(
                    lang,
                    'jobs.smiths',
                    settlement.jobs.smiths,
                    MAX_SMITHS * settlement.buildings.blacksmith.qty,
                ),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_SMITH,
                t(lang, 'jobs.smithPlus'),
                settlement.buildings.blacksmith.qty > 0 &&
                    settlement.jobs.smiths < MAX_SMITHS * settlement.buildings.blacksmith.qty &&
                    free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_SMITH,
                t(lang, 'jobs.smithMinus'),
                settlement.jobs.smiths > 0,
            );
            section.spacer(4);

            section.text(
                t(
                    lang,
                    'jobs.minters',
                    settlement.jobs.minters,
                    MAX_MINTERS * settlement.buildings.mint.qty,
                ),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_MINTER,
                t(lang, 'jobs.minterPlus'),
                settlement.buildings.mint.qty > 0 &&
                    settlement.jobs.minters < MAX_MINTERS * settlement.buildings.mint.qty &&
                    free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_MINTER,
                t(lang, 'jobs.minterMinus'),
                settlement.jobs.minters > 0,
            );
            section.spacer(4);
        }

        if (activeTabId === ACTION__MODAL__TAB__JOBS__SUPPORT) {
            section.text(
                t(
                    lang,
                    'jobs.builders',
                    settlement.jobs.builders,
                    MAX_BUILDERS * settlement.buildings.houses,
                ),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_BUILDER,
                t(lang, 'jobs.builderPlus'),
                settlement.jobs.builders < MAX_BUILDERS * settlement.buildings.houses && free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_BUILDER,
                t(lang, 'jobs.builderMinus'),
                settlement.jobs.builders > 0,
            );
            section.spacer(4);

            section.text(
                t(
                    lang,
                    'jobs.militia',
                    settlement.jobs.militia,
                    MAX_MILITIA * settlement.buildings.barracks.qty,
                ),
            );
            section.button(
                ACTION__SETTLEMENT__HIRE_MILITIA,
                t(lang, 'jobs.militiaPlus'),
                settlement.buildings.barracks.qty > 0 &&
                    settlement.jobs.militia < MAX_MILITIA * settlement.buildings.barracks.qty &&
                    free > 0,
            );
            section.button(
                ACTION__SETTLEMENT__FIRE_MILITIA,
                t(lang, 'jobs.militiaMinus'),
                settlement.jobs.militia > 0,
            );
            section.spacer(4);
        }
    });
}
